/**
 * Phase 8 · Cluster C-47 — Row-level access primitive for team-member
 * profile views.
 *
 * Spec: docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum G §34.1.
 *
 * The profile page at `/team/:orgUserId` and every query on
 * `teamMemberProfileService` funnel through `assertCanViewMemberProfile`
 * so **no UI path can read another member's activity / requisitions
 * without passing this gate** — the check is intentionally centralised
 * to make it easy to review (rg "assertCanViewMemberProfile" returns
 * one definition + every call site).
 *
 * Rules, in precedence order:
 *
 *   1. Self — a user can always see their own profile.
 *   2. Role — owner / admin see everyone inside their org.
 *   3. Hierarchy — senior_recruiter (the "manager" role in B2B's RBAC
 *      vocabulary) can see anyone who rolls up to them in
 *      `ggj_b2b_org_user_hierarchy` (the materialised view landed by
 *      migration 111). The descendant check is a single indexed lookup.
 *   4. Org-wide opt-in — `organizations.settings.open_profiles = true`
 *      lets peers see each other. Cached per-org for 30s (see
 *      `OpenProfilesCache`). Security-sensitive: we never cache the
 *      derived access decision across viewers, only the org-level flag
 *      which is orthogonal to viewer identity.
 *   5. Otherwise — denied. Callers throw `RbacDenied` so the UI can
 *      render `<AccessDeniedState>` in a single catch block.
 *
 * RLS defence-in-depth: the underlying Supabase tables
 * (`ggj_b2b_requisitions`, `ggj_b2b_candidates`, …) already enforce
 * `org_id = current_org_id()` via existing policies. This module is
 * purely the *scoping* primitive — it narrows the set of rows that are
 * allowed to be shown to a viewer **within** their org.
 */
import { supabase } from '@core/supabase/client';
import type { AuthUser, OrgRole } from '@core/types';

// ──────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────

/** Minimal shape every gate caller must supply — subset of `AuthUser`. */
export interface RbacViewer {
  /** `ggj_b2b_org_users.id` of the viewer; never the Keycloak sub. */
  orgUserId: string | null;
  /** Viewer's org (row in `ggj_b2b_organizations`). */
  orgId: string | null;
  /** Authoritative B2B roles (from the org row, not Keycloak). */
  roles: OrgRole[];
}

/**
 * Thrown by `assertCanViewMemberProfile`. `reason` is a machine-safe
 * tag the UI can switch on if/when we ship friendlier copy per rule.
 * Today the only value is `'OUT_OF_SCOPE'` — extending this enum is
 * deliberately cheap.
 */
export class RbacDenied extends Error {
  reason: 'OUT_OF_SCOPE';
  constructor(reason: 'OUT_OF_SCOPE' = 'OUT_OF_SCOPE', message = 'Access denied') {
    super(message);
    this.name = 'RbacDenied';
    this.reason = reason;
  }
}

/** Type-guard so callers can do `catch (err) { if (isRbacDenied(err)) … }`. */
export function isRbacDenied(err: unknown): err is RbacDenied {
  return err instanceof RbacDenied;
}

// ──────────────────────────────────────────────────────────────────
// Internals
// ──────────────────────────────────────────────────────────────────

const ELEVATED_ROLES: ReadonlySet<OrgRole> = new Set(['owner', 'admin']);

/**
 * Roles that get a *subtree* escalation via
 * `ggj_b2b_org_user_hierarchy`. B2B has no dedicated "manager" role so
 * this maps to senior_recruiter (the highest non-admin tier). If a
 * future migration adds `manager` to `OrgRole` it belongs here too —
 * the spec leaves room for that.
 */
const SUBTREE_ROLES: ReadonlySet<OrgRole> = new Set(['senior_recruiter']);

/** 30-second TTL keyed by orgId — per-org flag, viewer-independent. */
class OpenProfilesCache {
  private entries = new Map<string, { value: boolean; expiresAt: number }>();
  private readonly ttlMs = 30_000;

  async get(orgId: string): Promise<boolean> {
    const now = Date.now();
    const existing = this.entries.get(orgId);
    if (existing && existing.expiresAt > now) return existing.value;

    const { data, error } = await supabase
      .from('ggj_b2b_organizations')
      .select('settings')
      .eq('id', orgId)
      .maybeSingle();

    // On error or missing row we deliberately default to false — closed
    // by default is the right failure mode for a privacy control.
    const flag =
      !error &&
      data &&
      typeof data.settings === 'object' &&
      data.settings !== null &&
      (data.settings as Record<string, unknown>)['open_profiles'] === true;

    this.entries.set(orgId, { value: flag === true, expiresAt: now + this.ttlMs });
    return flag === true;
  }

  /** Clear the cache entry so the next read picks up a fresh value. */
  invalidate(orgId: string): void {
    this.entries.delete(orgId);
  }
}

const openProfilesCache = new OpenProfilesCache();

export function invalidateOpenProfilesCache(orgId: string): void {
  openProfilesCache.invalidate(orgId);
}

/**
 * One row hit = viewer is somewhere above target in the manager_id
 * graph (self-pairs at depth 0 are filtered out explicitly because the
 * self branch above short-circuits at a higher level). Hard-gates on
 * `org_id = viewer.orgId` so a stray cross-org manager mapping can
 * never leak rows — belt *and* braces.
 */
async function isDescendantOf(
  viewerOrgId: string,
  viewerOrgUserId: string,
  targetUserId: string,
): Promise<boolean> {
  const { data, error } = await supabase
    .from('ggj_b2b_org_user_hierarchy')
    .select('descendant_id', { head: false })
    .eq('org_id', viewerOrgId)
    .eq('ancestor_id', viewerOrgUserId)
    .eq('descendant_id', targetUserId)
    .limit(1);

  if (error) return false;
  return (data?.length ?? 0) > 0;
}

function hasRole(viewer: RbacViewer, wanted: ReadonlySet<OrgRole>): boolean {
  for (const r of viewer.roles) {
    if (wanted.has(r)) return true;
  }
  return false;
}

// ──────────────────────────────────────────────────────────────────
// Public API
// ──────────────────────────────────────────────────────────────────

/**
 * Returns true iff `viewer` is permitted to see `targetUserId`'s full
 * profile (all tabs: requisitions / candidates / interviews / …).
 *
 * The result is **not** cached — each call is ~1 Supabase round-trip
 * at worst. Callers that render hover cards for dozens of members can
 * memoise locally (e.g. React Query) but must key by `viewerId` too,
 * never across viewers.
 */
export async function canViewMemberProfile(
  viewer: RbacViewer,
  targetUserId: string,
): Promise<boolean> {
  if (!viewer.orgId || !viewer.orgUserId) return false;
  if (!targetUserId) return false;

  // 1. Self
  if (viewer.orgUserId === targetUserId) return true;

  // 2. Elevated role (admin / owner)
  if (hasRole(viewer, ELEVATED_ROLES)) return true;

  // 3. Hierarchy (manager-equivalent roles)
  if (hasRole(viewer, SUBTREE_ROLES)) {
    const inSubtree = await isDescendantOf(viewer.orgId, viewer.orgUserId, targetUserId);
    if (inSubtree) return true;
    // Fall through to the open-profiles check so a senior recruiter can
    // still see peers when the org has opted in (spec §34.1 rule 4).
  }

  // 4. Org-wide opt-in
  return openProfilesCache.get(viewer.orgId);
}

/**
 * Throws `RbacDenied('OUT_OF_SCOPE')` when the viewer fails every rule
 * in `canViewMemberProfile`. Prefer this in service code because the
 * UI wraps a single catch-all around the page body — the typed error
 * is what `<AccessDeniedState>` keys off.
 */
export async function assertCanViewMemberProfile(
  viewer: RbacViewer,
  targetUserId: string,
): Promise<void> {
  const ok = await canViewMemberProfile(viewer, targetUserId);
  if (!ok) {
    throw new RbacDenied('OUT_OF_SCOPE', 'You do not have access to this member profile.');
  }
}

/** Narrow helper: build an `RbacViewer` from the app-wide AuthUser. */
export function viewerFromAuthUser(user: AuthUser | null | undefined): RbacViewer {
  return {
    orgUserId: user?.orgUserId ?? null,
    orgId: user?.orgId ?? null,
    roles: user?.roles ?? [],
  };
}
